import React, { useState, useRef, useEffect, useMemo } from "react";
import { Loader2, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import betaAPI from "v2/api";
import useInfiniteScroll from "v2/hooks/useInfiniteScroll";
import SearchInput from "../SuperUserDashboard/SearchInput";
import BatchRow from "./BatchRow";
import WearableRow from "./WearableRow";
import FilterSelect, { getWearableSortOptions } from "./FilterSelect";
import BatchFormModal from "./BatchFormModal";
import WearableFormModal from "./WearableFormModal";
import {
  getStatusOptions,
  getStageOptions,
  getUnitTypeOptions,
} from "./constants";

// Labels are translated at render time: admin.manufacturing.tabs.<id>.label
const TABS = [{ id: "batches" }, { id: "wearables" }];

const ManufacturingTabbedSection = ({ onDataChange }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("batches");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });
  const [tabCounts, setTabCounts] = useState({});
  const tabsRef = useRef({});

  // Wearable filters
  const [statusFilter, setStatusFilter] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  const [unitTypeFilter, setUnitTypeFilter] = useState("");
  const [batchFilter, setBatchFilter] = useState("");
  const [sortKey, setSortKey] = useState(""); // "" = API default (newest first)
  const [batchOptions, setBatchOptions] = useState([]);

  // Create modals
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [showWearableModal, setShowWearableModal] = useState(false);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Reset search/filters when switching tabs
  useEffect(() => {
    setSearch("");
    setDebouncedSearch("");
  }, [activeTab]);

  // Sliding indicator
  useEffect(() => {
    const el = tabsRef.current[activeTab];
    if (el) {
      setIndicatorStyle({ left: el.offsetLeft, width: el.offsetWidth });
    }
  }, [activeTab, tabCounts]);

  // Load batch options for the wearable filter
  useEffect(() => {
    (async () => {
      try {
        const res = await betaAPI.getBatches({ page: 1, pageSize: 100 });
        setBatchOptions(
          (res.data?.results || []).map((b) => ({ id: b.id, label: b.code }))
        );
      } catch {
        // non-critical
      }
    })();
  }, []);

  const fetchFn = useMemo(
    () => (activeTab === "batches" ? betaAPI.getBatches : betaAPI.getWearables),
    [activeTab]
  );

  const extraParams = useMemo(
    () =>
      activeTab === "wearables"
        ? {
            status: statusFilter,
            validationStage: stageFilter,
            unitType: unitTypeFilter,
            batchId: batchFilter,
            ordering: sortKey,
          }
        : {},
    [activeTab, statusFilter, stageFilter, unitTypeFilter, batchFilter, sortKey]
  );

  const { items, loading, hasMore, totalCount, lastItemRef, refresh } =
    useInfiniteScroll({
      fetchFn,
      search: debouncedSearch,
      pageSize: 20,
      extraParams,
    });

  // Keep the active tab badge in sync with the live total
  useEffect(() => {
    if (totalCount > 0) {
      setTabCounts((prev) => ({ ...prev, [activeTab]: totalCount }));
    }
  }, [activeTab, totalCount]);

  const handleCreated = () => {
    refresh();
    onDataChange?.();
  };

  return (
    <div className="overflow-hidden rounded-xl border border-[#F3F4F6] bg-white">
      {/* Header: tabs + controls */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#F3F4F6] px-4">
        {/* Tabs */}
        <div className="relative flex">
          <div
            className="absolute bottom-0 h-0.5 bg-[#E07A3A] transition-all duration-300 ease-out"
            style={{ left: indicatorStyle.left, width: indicatorStyle.width }}
          />
          {TABS.map((tab) => {
            const isActive = activeTab === tab.id;
            const count = tabCounts[tab.id];
            return (
              <button
                key={tab.id}
                ref={(el) => (tabsRef.current[tab.id] = el)}
                onClick={() => setActiveTab(tab.id)}
                className="relative px-4 py-3.5 text-sm font-semibold transition-colors duration-200"
                style={{ color: isActive ? "#E07A3A" : "#6B7280" }}
              >
                {t(`admin.manufacturing.tabs.${tab.id}.label`)}
                {count > 0 && (
                  <span
                    className="ml-2 inline-block rounded-full px-2 py-0.5 text-xs font-medium transition-colors duration-200"
                    style={{
                      backgroundColor: isActive ? "#FFF3ED" : "#F3F4F6",
                      color: isActive ? "#E07A3A" : "#6B7280",
                    }}
                  >
                    {count.toLocaleString()}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Controls */}
        <div className="flex flex-wrap items-center gap-2 py-2">
          {activeTab === "wearables" && (
            <>
              <FilterSelect
                value={statusFilter}
                onChange={setStatusFilter}
                options={getStatusOptions()}
                allLabel={t("admin.manufacturing.filters.allStatuses")}
              />
              <FilterSelect
                value={stageFilter}
                onChange={setStageFilter}
                options={getStageOptions()}
                allLabel={t("admin.manufacturing.filters.allStages")}
              />
              <FilterSelect
                value={unitTypeFilter}
                onChange={setUnitTypeFilter}
                options={getUnitTypeOptions()}
                allLabel={t("admin.manufacturing.filters.allTypes")}
              />
              <FilterSelect
                value={batchFilter}
                onChange={setBatchFilter}
                options={batchOptions}
                allLabel={t("admin.manufacturing.filters.allBatches")}
              />
              <FilterSelect
                value={sortKey}
                onChange={setSortKey}
                options={getWearableSortOptions(t)}
                allLabel={t("admin.manufacturing.sort.newestFirst")}
              />
            </>
          )}

          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder={
              activeTab === "batches"
                ? t("admin.manufacturing.tabs.batches.searchPlaceholder")
                : t("admin.manufacturing.tabs.wearables.searchPlaceholder")
            }
          />

          <button
            onClick={() =>
              activeTab === "batches"
                ? setShowBatchModal(true)
                : setShowWearableModal(true)
            }
            className="flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-sm font-semibold text-white transition duration-150"
            style={{
              background: "linear-gradient(135deg, #E8854A 0%, #D66B2E 100%)",
            }}
          >
            <Plus size={15} />
            {activeTab === "batches"
              ? t("admin.manufacturing.tabs.batches.create")
              : t("admin.manufacturing.tabs.wearables.create")}
          </button>
        </div>
      </div>

      {/* List */}
      <div className="max-h-[640px] overflow-y-auto">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
          >
            {items.map((item, index) => {
              const isLast = index === items.length - 1;
              const ref = isLast ? lastItemRef : null;
              return activeTab === "batches" ? (
                <BatchRow
                  key={item.id}
                  batch={item}
                  isLast={isLast}
                  ref={ref}
                />
              ) : (
                <WearableRow
                  key={item.ble_mac}
                  wearable={item}
                  isLast={isLast}
                  ref={ref}
                />
              );
            })}

            {loading && (
              <div className="flex items-center justify-center py-4">
                <Loader2 size={20} className="animate-spin text-[#E07A3A]" />
              </div>
            )}

            {!loading && items.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <p className="text-sm text-[#6B7280]">
                  {debouncedSearch
                    ? t(`admin.manufacturing.tabs.${activeTab}.emptySearch`, {
                        search: debouncedSearch,
                      })
                    : t(`admin.manufacturing.tabs.${activeTab}.empty`)}
                </p>
              </div>
            )}

            {!loading && items.length > 0 && !hasMore && (
              <div className="py-3 text-center text-xs text-[#9CA3AF]">
                {t(`admin.manufacturing.tabs.${activeTab}.showingAll`, {
                  total: totalCount.toLocaleString(),
                })}
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Create modals */}
      <BatchFormModal
        show={showBatchModal}
        onClose={() => setShowBatchModal(false)}
        onSaved={handleCreated}
      />
      <WearableFormModal
        show={showWearableModal}
        onClose={() => setShowWearableModal(false)}
        onSaved={handleCreated}
      />
    </div>
  );
};

export default ManufacturingTabbedSection;
